"use client";

import * as React from "react";
import { Message } from "@/lib/core/crm/types";
import { useContactByEmail } from "@/features/contacts/hooks";
import { useQuery } from "@tanstack/react-query";
import { getBrowserSupabaseClient } from "@wtc/supabase";
import { Badge, Button, Skeleton, useGlobalModalStack, MODAL_IDS } from "@wtc/ui";
import { Mail, Building2, TrendingUp, Users } from "lucide-react";
import { cn } from "@/lib/utils";

interface ContactContextCardProps {
  message: Message;
  className?: string;
}

interface ContactInfo {
  email: string;
  name: string;
  type: "from" | "to" | "cc" | "bcc";
}

function SingleContactCard({ email, name, type }: ContactInfo) {
  const { data: contact, isLoading: contactLoading } = useContactByEmail(email);
  const { openModal } = useGlobalModalStack();

  // Fetch deals for the contact's company
  const { data: deals, isLoading: dealsLoading } = useQuery({
    queryKey: ["deals-by-company", contact?.company_id],
    queryFn: async () => {
      if (!contact?.company_id) return [];
      const supabase = getBrowserSupabaseClient();
      const { data, error } = await supabase
        .from("deals")
        .select("id, title, stage, revenue, close_date")
        .eq("company_id", contact.company_id)
        .order("created_at", { ascending: false })
        .limit(3);

      if (error) {
        console.error("Error fetching deals:", error);
        return [];
      }
      return data || [];
    },
    enabled: !!contact?.company_id,
    staleTime: 5 * 60 * 1000,
  });

  // Fetch email count for this contact
  const { data: emailCount, isLoading: emailCountLoading } = useQuery({
    queryKey: ["email-count", contact?.id],
    queryFn: async () => {
      if (!contact?.id) return 0;
      const supabase = getBrowserSupabaseClient();
      const { count, error } = await supabase
        .from("communications")
        .select("*", { count: "exact", head: true })
        .eq("contact_id", contact.id)
        .eq("type", "email");

      if (error) {
        console.error("Error fetching email count:", error);
        return 0;
      }
      return count || 0;
    },
    enabled: !!contact?.id,
    staleTime: 5 * 60 * 1000,
  });

  const handleCardClick = React.useCallback(() => {
    if (contact?.id) {
      openModal(MODAL_IDS.contacts.detail, { contactId: contact.id });
    }
  }, [contact?.id, openModal]);

  const handleCompanyClick = React.useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      if (contact?.company_id) {
        openModal(MODAL_IDS.companies.detail, { companyId: contact.company_id });
      }
    },
    [contact?.company_id, openModal]
  );

  const statusColor = {
    prospect: "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200",
    customer: "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200",
    lead: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200",
    inactive: "bg-gray-100 text-gray-800 dark:bg-gray-900 dark:text-gray-200",
  };

  const stageColor = {
    prospect: "bg-blue-50 text-blue-700 dark:bg-blue-950/40 dark:text-blue-300",
    qualified: "bg-purple-50 text-purple-700 dark:bg-purple-950/40 dark:text-purple-300",
    proposal: "bg-orange-50 text-orange-700 dark:bg-orange-950/40 dark:text-orange-300",
    negotiation: "bg-yellow-50 text-yellow-700 dark:bg-yellow-950/40 dark:text-yellow-300",
    won: "bg-green-50 text-green-700 dark:bg-green-950/40 dark:text-green-300",
    lost: "bg-red-50 text-red-700 dark:bg-red-950/40 dark:text-red-300",
  };

  const typeColor = {
    from: "border-l-4 border-l-blue-500",
    to: "border-l-4 border-l-green-500",
    cc: "border-l-4 border-l-yellow-500",
    bcc: "border-l-4 border-l-purple-500",
  };

  if (contactLoading) {
    return (
      <div className={cn("space-y-2 p-3 border rounded-lg bg-muted/30", typeColor[type])}>
        <Skeleton className="h-4 w-24" />
        <Skeleton className="h-3 w-20" />
      </div>
    );
  }

  if (!contact) {
    return (
      <div className={cn("p-3 border rounded-lg bg-muted/30 text-xs text-muted-foreground", typeColor[type])}>
        <div className="font-medium">{name || email}</div>
        <p className="text-xs truncate">{email}</p>
        <div className="text-xs mt-1 opacity-75">No contact record</div>
      </div>
    );
  }

  return (
    <div
      className={cn(
        "space-y-2 p-3 border rounded-lg bg-card cursor-pointer transition-all hover:shadow-md hover:border-primary/50",
        typeColor[type]
      )}
      onClick={handleCardClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          handleCardClick();
        }
      }}
    >
      {/* Contact Header */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <h4 className="font-semibold text-xs truncate">{contact.name}</h4>
          <p className="text-xs text-muted-foreground truncate">{contact.email}</p>
        </div>
        <Badge className={cn("text-xs", statusColor[contact.status as keyof typeof statusColor] || statusColor.prospect)}>
          {contact.status}
        </Badge>
      </div>

      {/* Company Info */}
      {contact.company_name && (
        <div
          className="flex items-center gap-2 text-xs cursor-pointer transition-colors hover:text-primary"
          onClick={handleCompanyClick}
          role="button"
          tabIndex={0}
          onKeyDown={(e) => {
            if (e.key === "Enter" || e.key === " ") {
              handleCompanyClick(e as any);
            }
          }}
        >
          <Building2 className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <span className="truncate text-muted-foreground hover:text-primary">{contact.company_name}</span>
        </div>
      )}

      {/* Email Count */}
      {!emailCountLoading && emailCount !== undefined && emailCount > 0 && (
        <div className="flex items-center gap-2 text-xs">
          <Mail className="h-3 w-3 text-muted-foreground flex-shrink-0" />
          <span className="text-muted-foreground">{emailCount} emails</span>
        </div>
      )}

      {/* Recent Deals */}
      {deals && deals.length > 0 && (
        <div className="space-y-1">
          <div className="flex items-center gap-1 text-xs font-semibold text-muted-foreground">
            <TrendingUp className="h-2 w-2" />
            Deals
          </div>
          <div className="space-y-0.5">
            {deals.map((deal) => (
              <div
                key={deal.id}
                className={cn(
                  "p-1.5 rounded text-xs",
                  stageColor[deal.stage as keyof typeof stageColor] || stageColor.prospect
                )}
              >
                <div className="font-medium truncate">{deal.title}</div>
                <div className="flex items-center justify-between gap-1 text-xs opacity-75">
                  <span>{deal.stage}</span>
                  {deal.revenue && <span className="font-semibold">${(deal.revenue / 1000).toFixed(0)}k</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

export function ContactContextCard({ message, className }: ContactContextCardProps) {
  // Collect all email addresses from the message
  const allContacts: ContactInfo[] = [
    {
      email: message.sender.email,
      name: message.sender.name,
      type: "from",
    },
    ...message.recipients.map((r) => ({
      email: r.email,
      name: r.name,
      type: "to" as const,
    })),
  ];

  return (
    <div className={cn("space-y-3", className)}>
      {/* Header */}
      <div className="flex items-center gap-2 text-sm font-semibold text-muted-foreground px-1">
        <Users className="h-4 w-4" />
        Message Participants
      </div>

      {/* Contact Cards */}
      <div className="space-y-2">
        {allContacts.map((contact, index) => (
          <SingleContactCard
            key={`${contact.email}-${index}`}
            email={contact.email}
            name={contact.name}
            type={contact.type}
          />
        ))}
      </div>
    </div>
  );
}
